import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import {
  MessageCircle, Mail, MessageSquareText, IndianRupee, CheckCircle2, Eye, XCircle, MinusCircle, Plus,
} from "lucide-react";
import { toast } from "sonner";
import BroadcastComposerModal from "../components/BroadcastComposerModal";
import { Button } from "../components/ui/button";

// Same brand palette as AnalyticsCharts.jsx, for visual consistency across
// every analytics-flavoured page in the app.
const NAVY = "#0a1f44";
const BLUE = "#0284c7";
const EMERALD = "#10b981";
const AMBER = "#f59e0b";

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", icon: MessageCircle, color: EMERALD },
  email: { label: "Email", icon: Mail, color: BLUE },
  sms: { label: "SMS", icon: MessageSquareText, color: AMBER },
};

const nf = (n) => Number(n || 0).toLocaleString("en-IN");
const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_META = {
  delivered: { label: "Delivered", cls: "bg-emerald-100 text-emerald-800", Icon: CheckCircle2 },
  partial: { label: "Partially delivered", cls: "bg-amber-100 text-amber-800", Icon: MinusCircle },
  failed: { label: "Failed", cls: "bg-red-100 text-red-800", Icon: XCircle },
};

function quotaTone(pct) {
  if (pct >= 90) return { bar: "bg-red-500", text: "text-red-700" };
  if (pct >= 70) return { bar: "bg-amber-500", text: "text-amber-700" };
  return { bar: "bg-emerald-500", text: "text-emerald-700" };
}

export default function CampaignUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courses, setCourses] = useState([]);
  const [composerOpen, setComposerOpen] = useState(false);

  const load = () =>
    api.get("/campaigns/usage")
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Could not load campaign usage."));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    Promise.all([
      load(),
      api.get("/analytics/filter-options").then((r) => { if (alive) setCourses(r.data.courses || []); }).catch(() => {}),
    ]).finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) {
    return (
      <div className="py-24 flex justify-center">
        <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-800 rounded-full animate-spin" />
      </div>
    );
  }
  if (!data) {
    return <div className="py-24 text-center text-sm text-slate-400">Could not load campaign usage right now.</div>;
  }

  const { channels, estimated_total_cost, daily, recent_logs, month } = data;

  return (
    <div className="space-y-6" data-testid="campaign-usage-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Campaigns & Messaging Usage</h1>
          <p className="text-sm text-slate-500 mt-1">
            WhatsApp, Email and SMS consumption and deliverability for {month}.
          </p>
        </div>
        <Button onClick={() => setComposerOpen(true)} data-testid="new-broadcast-button">
          <Plus className="w-4 h-4 mr-1.5" /> New Broadcast
        </Button>
      </div>

      <BroadcastComposerModal
        open={composerOpen}
        onClose={() => setComposerOpen(false)}
        onSuccess={load}
        channels={channels}
        courses={courses}
      />

      {/* Top row: quota cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <QuotaCard channelKey="whatsapp" block={channels.whatsapp} />
        <QuotaCard channelKey="email" block={channels.email} />
        <CostCard channels={channels} total={estimated_total_cost} />
      </div>

      {/* Middle row: visualizers */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <DailyConsumptionChart daily={daily} />
        </div>
        <DeliverabilityPills channels={channels} />
      </div>

      {/* Bottom: broadcast & automated log */}
      <BroadcastLogTable rows={recent_logs} />
    </div>
  );
}

function QuotaCard({ channelKey, block }) {
  const meta = CHANNEL_META[channelKey];
  const Icon = meta.icon;
  const tone = quotaTone(block.quota_consumed_pct);
  return (
    <Card className="p-5 border-slate-200" data-testid={`quota-card-${channelKey}`}>
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4" style={{ color: meta.color }} />
        </div>
        <h3 className="text-sm font-semibold text-slate-800">{meta.label} Usage</h3>
      </div>

      <div className="flex items-baseline justify-between mb-1.5">
        <span className="text-2xl font-bold text-slate-900" data-testid={`quota-used-${channelKey}`}>
          {nf(block.used)}
        </span>
        <span className={`text-sm font-medium ${tone.text}`}>{block.quota_consumed_pct}%</span>
      </div>
      <Progress value={Math.min(100, block.quota_consumed_pct)} className="h-2" />
      <p className="text-xs text-slate-400 mt-1.5">
        {nf(block.used)} / {nf(block.quota)} consumed this month
      </p>

      <div className="flex flex-wrap gap-1.5 mt-3.5">
        <Badge className="bg-indigo-100 text-indigo-800" data-testid={`quota-marketing-${channelKey}`}>
          Marketing · {nf(block.marketing.sent)}
        </Badge>
        <Badge className="bg-slate-100 text-slate-700" data-testid={`quota-utility-${channelKey}`}>
          Utility · {nf(block.utility.sent)}
        </Badge>
      </div>
    </Card>
  );
}

function CostCard({ channels, total }) {
  return (
    <Card className="p-5 border-slate-200" data-testid="cost-card">
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
          <IndianRupee className="w-4 h-4 text-slate-600" />
        </div>
        <h3 className="text-sm font-semibold text-slate-800">Estimated Network Cost</h3>
      </div>
      <div className="text-2xl font-bold text-slate-900" data-testid="cost-total">{money(total)}</div>
      <p className="text-xs text-slate-400 mt-1">this month, across all channels</p>

      <div className="mt-3.5 space-y-1.5">
        {Object.entries(channels).map(([key, block]) => {
          const meta = CHANNEL_META[key];
          return (
            <div key={key} className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1.5 text-slate-500">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} /> {meta.label}
              </span>
              <span className="font-medium text-slate-700">{money(block.estimated_cost)}</span>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function DailyConsumptionChart({ daily }) {
  const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short" }); }
    catch { return d; }
  };
  const chartData = (daily || []).map((d) => ({ ...d, label: fmtDate(d.date) }));

  return (
    <Card className="p-5 border-slate-200 h-full" data-testid="daily-consumption-chart">
      <h3 className="text-sm font-semibold text-slate-800 mb-4">Daily Consumption — last 30 days</h3>
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={chartData} margin={{ top: 4, right: 8, left: -20, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke="#EEF2F7" />
          <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false}
            interval={Math.max(0, Math.floor(chartData.length / 8) - 1)} />
          <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} tickLine={false} axisLine={false} />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #E2E8F0" }}
            labelFormatter={(l) => l}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar dataKey="whatsapp" name="WhatsApp" stackId="ch" fill={CHANNEL_META.whatsapp.color} radius={[0, 0, 0, 0]} />
          <Bar dataKey="email" name="Email" stackId="ch" fill={CHANNEL_META.email.color} />
          <Bar dataKey="sms" name="SMS" stackId="ch" fill={CHANNEL_META.sms.color} radius={[3, 3, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </Card>
  );
}

function DeliverabilityPills({ channels }) {
  return (
    <Card className="p-5 border-slate-200 h-full" data-testid="deliverability-pills">
      <h3 className="text-sm font-semibold text-slate-800 mb-4">Deliverability</h3>
      <div className="space-y-3.5">
        {Object.entries(channels).map(([key, block]) => {
          const meta = CHANNEL_META[key];
          const Icon = meta.icon;
          const openLabel = key === "email" ? "Open rate" : key === "whatsapp" ? "Read rate" : null;
          return (
            <div key={key} data-testid={`deliverability-${key}`}>
              <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600 mb-1.5">
                <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} /> {meta.label}
              </div>
              <div className="flex flex-wrap gap-1.5">
                <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Delivery {block.delivery_rate_pct != null ? `${block.delivery_rate_pct}%` : "—"}
                </Badge>
                {openLabel && (
                  <Badge className="bg-sky-50 text-sky-700 border border-sky-200">
                    <Eye className="w-3 h-3 mr-1 inline" /> {openLabel} {block.read_rate_pct != null ? `${block.read_rate_pct}%` : "—"}
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function BroadcastLogTable({ rows }) {
  return (
    <Card className="p-5 border-slate-200" data-testid="broadcast-log-table">
      <h3 className="text-sm font-semibold text-slate-800 mb-4">Broadcast & Automated Log</h3>
      {(!rows || rows.length === 0) ? (
        <div className="text-sm text-slate-400 text-center py-10">No dispatches recorded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Message Type</TableHead>
                <TableHead>Channel</TableHead>
                <TableHead className="text-right">Recipients</TableHead>
                <TableHead className="text-right">Delivery %</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r, i) => {
                const st = STATUS_META[r.status] || STATUS_META.delivered;
                const StIcon = st.Icon;
                const meta = CHANNEL_META[r.channel] || {};
                const ChIcon = meta.icon;
                return (
                  <TableRow key={i} data-testid={`broadcast-row-${i}`}>
                    <TableCell className="whitespace-nowrap text-slate-500">{r.date}</TableCell>
                    <TableCell className="font-medium text-slate-800">
                      {r.message_type}
                      <Badge className="ml-2 bg-slate-100 text-slate-500 text-[10px]">{r.category}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1.5">
                        {ChIcon && <ChIcon className="w-3.5 h-3.5" style={{ color: meta.color }} />}
                        {meta.label || r.channel}
                      </span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{nf(r.recipients)}</TableCell>
                    <TableCell className="text-right tabular-nums">{r.delivery_pct}%</TableCell>
                    <TableCell>
                      <Badge className={st.cls}><StIcon className="w-3 h-3 mr-1 inline" />{st.label}</Badge>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </table>
        </div>
      )}
    </Card>
  );
}
